/**
 * What clients would read: the server's anonymized version of a saved case, or, while there are unsaved edits, a
 * live approximation of it made here (the saved version is the one that is shared).
 */

import { useAnonymizedCase } from '@/api/queries/cases';
import type { CaseTextKey } from '@/api/types/cases';
import { FormError } from '@/ui/StateCard';

import { CaseBody, type CaseContent } from './CaseBody';
import { anonymize } from './anonymize';
import { caseBody, namesOf, type CaseForm } from './caseForm';

export function liveAnonymized(form: CaseForm): CaseContent {
  const names = namesOf(form);
  const label = form.anonymized_label.trim() || 'the client';
  const swap = (v: string | null | undefined) => (v ? anonymize(v, names, label) : null);
  const metrics = (caseBody(form).metrics ?? []).map((m) => ({ ...m, label: anonymize(m.label, names, label), unit: swap(m.unit) }));
  const text = (key: CaseTextKey) => anonymize(form[key].trim(), names, label);
  return { metrics, situation: text('situation'), objective: text('objective'), approach: text('approach'), achievements: text('achievements') };
}

function ServerPreview({ caseId }: { caseId: string }) {
  const preview = useAnonymizedCase(caseId);
  if (preview.isPending) return <p className="hint" role="status">Loading the anonymized version…</p>;
  if (preview.isError) return <FormError error={preview.error} />;
  return (
    <>
      <h4>{preview.data.label}</h4>
      <CaseBody content={preview.data} label={`${preview.data.label} (anonymized)`} />
    </>
  );
}

export function AnonymizedPreview({ caseId, dirty, form }: { caseId: string | null; dirty: boolean; form: CaseForm }) {
  const label = form.anonymized_label.trim() || 'No label yet';
  return (
    <aside className="case-preview" aria-label="Anonymized preview">
      <h4>Anonymized preview</h4>
      <p className="hint">
        The client name and aliases are replaced by the label. Industry and country stay visible: check that together they do not
        identify the client.
      </p>
      {caseId && !dirty ? (
        <ServerPreview caseId={caseId} />
      ) : (
        <>
          <p className="hint" role="status">{caseId ? 'Preview of your unsaved edits; save to see the version clients get.' : 'Preview of this new case; save it to share it.'}</p>
          <h4>{label}</h4>
          <CaseBody content={liveAnonymized(form)} label={`${label} (anonymized)`} />
        </>
      )}
    </aside>
  );
}
